import React, { useState, useRef, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Play, ArrowRight, Clock } from "lucide-react";
import { mediaUrl } from "@/lib/api";

// Brand tones (per spec)
const IVORY = "#F6F1E8";
const GOLD = "#B08A3C";
const STEEL = "#3F5968";
const CHARCOAL = "#33383D";

// 2x2 grid variant: shows up to 4 items simultaneously, no arrows / carousel.
export function FeaturedGrid({ slides }) {
  const items = slides.slice(0, 6);
  if (items.length === 0) return null;
  return (
    <section aria-label="Featured articles" data-testid="featured-grid">
      <div className="grid gap-6 sm:grid-cols-2 sm:gap-7 lg:grid-cols-3 lg:gap-8">
        {items.map((s, i) => (
          <article
            key={s.id || i}
            className="flex flex-col border p-4 sm:p-5"
            style={{ borderColor: `${GOLD}66`, backgroundColor: IVORY, boxShadow: `0 12px 44px -30px ${STEEL}80` }}
            data-testid={`grid-item-${i}`}
          >
            <Link
              to={s.href}
              className="group relative block aspect-video w-full overflow-hidden focus:outline-none focus-visible:ring-2"
              style={{ backgroundColor: STEEL, ["--tw-ring-color"]: GOLD }}
              data-testid={`grid-media-${i}`}
            >
              {s.image ? (
                <img
                  src={mediaUrl(s.image)}
                  alt={s.alt || s.title}
                  loading="lazy"
                  className="h-full w-full object-cover object-center transition-transform duration-[700ms] ease-out group-hover:scale-[1.03]"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center" style={{ backgroundColor: STEEL }}>
                  <span className="font-serif text-6xl" style={{ color: `${GOLD}66` }}>GF</span>
                </div>
              )}
              {s.type === "video" && (
                <span className="absolute inset-0 flex items-center justify-center">
                  <span
                    className="flex h-14 w-14 items-center justify-center rounded-full border transition-colors duration-300 group-hover:bg-[#3F5968]"
                    style={{ backgroundColor: IVORY, borderColor: GOLD }}
                  >
                    <Play className="ml-0.5 h-6 w-6" style={{ color: STEEL }} fill={STEEL} />
                  </span>
                </span>
              )}
              {s.type === "video" && s.duration && (
                <span
                  className="absolute bottom-3 right-3 flex items-center gap-1 border px-2 py-0.5 font-sans text-xs"
                  style={{ backgroundColor: IVORY, borderColor: `${GOLD}80`, color: STEEL }}
                >
                  <Clock className="h-3 w-3" /> {s.duration}
                </span>
              )}
            </Link>

            <div className="mt-5 flex min-w-0 flex-1 flex-col">
              <p className="font-sans text-[13px] font-semibold uppercase" style={{ color: GOLD, letterSpacing: "0.14em" }}>
                {s.type === "video" ? "Featured Video" : "Featured Article"}
                {s.category ? ` · ${s.category}` : ""}
              </p>
              <h3 className="mt-3 font-serif font-light" style={{ color: STEEL, fontSize: "clamp(22px, 2.2vw, 28px)", lineHeight: 1.15 }}>
                <Link to={s.href} className="transition-colors hover:opacity-80 focus:outline-none focus-visible:underline">
                  {s.title}
                </Link>
              </h3>
              {s.description && (
                <p className="mt-3 font-sans" style={{ color: CHARCOAL, fontSize: "clamp(15px, 1.2vw, 16px)", lineHeight: 1.6 }}>
                  <span className="line-clamp-2">{s.description}</span>
                </p>
              )}
              {s.meta && (
                <p className="mt-3 font-sans text-xs tracking-wide" style={{ color: `${STEEL}99` }}>{s.meta}</p>
              )}
              <Link
                to={s.href}
                className="group mt-5 inline-flex items-center gap-2 font-sans text-sm font-semibold tracking-wide transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                style={{ color: STEEL, ["--tw-ring-color"]: GOLD }}
                data-testid={`grid-action-${i}`}
              >
                {s.type === "video" ? "Watch video" : "Read article"}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" style={{ color: GOLD }} />
              </Link>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

// A single-window horizontal slider. One large featured article/video is visible;
// neighbours sit outside the frame and slide in via a transform-based transition.
export function FeaturedSlider({ slides }) {
  const [index, setIndex] = useState(0);
  const [reduced, setReduced] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [arrowTop, setArrowTop] = useState(null);
  const touchX = useRef(null);
  const windowRef = useRef(null);
  const mediaRef = useRef(null);
  const count = slides.length;

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduced(mq.matches);
    on();
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const on = () => setIsMobile(mq.matches);
    on();
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);

  useEffect(() => {
    const compute = () => {
      if (!windowRef.current || !mediaRef.current) return;
      const w = windowRef.current.getBoundingClientRect();
      const m = mediaRef.current.getBoundingClientRect();
      setArrowTop(m.bottom - w.top - 18); // sit over the lower edge of the media
    };
    compute();
    const ro = new ResizeObserver(compute);
    if (mediaRef.current) ro.observe(mediaRef.current);
    window.addEventListener("resize", compute);
    return () => { ro.disconnect(); window.removeEventListener("resize", compute); };
  }, [count]);

  const goTo = useCallback((i) => setIndex(((i % count) + count) % count), [count]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + count) % count), [count]);
  const next = useCallback(() => setIndex((i) => (i + 1) % count), [count]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
    else if (e.key === "ArrowRight") { e.preventDefault(); next(); }
  };
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 45) (dx < 0 ? next : prev)();
    touchX.current = null;
  };

  useEffect(() => { if (index > count - 1) setIndex(0); }, [count, index]);
  if (count === 0) return null;

  const ctrlBase =
    "flex h-11 w-11 items-center justify-center rounded-full border transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2";

  return (
    <section
      className="relative outline-none"
      tabIndex={0}
      onKeyDown={onKeyDown}
      role="group"
      aria-roledescription="carousel"
      aria-label="Featured articles and videos"
      data-testid="featured-slider"
    >
      {/* Live region for screen readers */}
      <div aria-live="polite" className="sr-only" data-testid="slider-live">
        Slide {index + 1} of {count}: {slides[index]?.title}
      </div>

      {/* Framed window */}
      <div className="relative" ref={windowRef}>
        {/* Viewport */}
        <div
          className="overflow-hidden border"
          style={{ borderColor: `${GOLD}66`, backgroundColor: IVORY, boxShadow: `0 12px 44px -30px ${STEEL}80` }}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {/* Track */}
          <div
            className="flex"
            style={{
              transform: `translateX(-${index * 100}%)`,
              transitionProperty: "transform",
              transitionDuration: reduced ? "0ms" : "550ms",
              transitionTimingFunction: "cubic-bezier(0, 0, 0.2, 1)",
            }}
            data-testid="slider-track"
          >
            {slides.map((s, i) => {
              const active = i === index;
              return (
                <div
                  key={s.id || i}
                  className="w-full shrink-0"
                  role="group"
                  aria-roledescription="slide"
                  aria-label={`${i + 1} of ${count}`}
                  aria-hidden={!active}
                  data-testid={`slide-${i}`}
                >
                  <div className="grid items-center gap-6 p-5 sm:p-7 lg:grid-cols-[47%_1fr] lg:gap-12 lg:px-14 lg:py-20">
                    {/* Media (16:9) */}
                    <Link
                      to={s.href}
                      ref={i === 0 ? mediaRef : undefined}
                      tabIndex={active ? 0 : -1}
                      aria-hidden={!active}
                      className="group relative block aspect-video w-full overflow-hidden focus:outline-none focus-visible:ring-2"
                      style={{ backgroundColor: STEEL, ["--tw-ring-color"]: GOLD }}
                    >
                      {s.image ? (
                        <img
                          src={mediaUrl(s.image)}
                          alt={s.alt || s.title}
                          loading="lazy"
                          className="h-full w-full object-cover object-center transition-transform duration-[700ms] ease-out group-hover:scale-[1.03]"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center" style={{ backgroundColor: STEEL }}>
                          <span className="font-serif text-6xl" style={{ color: `${GOLD}66` }}>GF</span>
                        </div>
                      )}
                      {s.type === "video" && (
                        <span className="absolute inset-0 flex items-center justify-center">
                          <span
                            className="flex h-14 w-14 items-center justify-center rounded-full border transition-colors duration-300 group-hover:bg-[#3F5968]"
                            style={{ backgroundColor: IVORY, borderColor: GOLD }}
                          >
                            <Play className="ml-0.5 h-6 w-6" style={{ color: STEEL }} fill={STEEL} />
                          </span>
                        </span>
                      )}
                      {s.type === "video" && s.duration && (
                        <span
                          className="absolute bottom-3 right-3 flex items-center gap-1 border px-2 py-0.5 font-sans text-xs"
                          style={{ backgroundColor: IVORY, borderColor: `${GOLD}80`, color: STEEL }}
                        >
                          <Clock className="h-3 w-3" /> {s.duration}
                        </span>
                      )}
                    </Link>

                    {/* Text */}
                    <div className="min-w-0">
                      <p
                        className="font-sans text-[13px] font-semibold uppercase"
                        style={{ color: GOLD, letterSpacing: "0.14em" }}
                      >
                        {s.type === "video" ? "Featured Video" : "Featured Article"}
                        {s.category ? ` · ${s.category}` : ""}
                      </p>
                      <h3
                        className="mt-4 font-serif font-light"
                        style={{ color: STEEL, fontSize: "clamp(30px, 5vw, 48px)", lineHeight: 1.1, maxWidth: "18ch" }}
                      >
                        <Link to={s.href} tabIndex={active ? 0 : -1} aria-hidden={!active} className="transition-colors hover:opacity-80 focus:outline-none focus-visible:underline">
                          {s.title}
                        </Link>
                      </h3>
                      {s.description && (
                        <p
                          className="mt-5 font-sans"
                          style={{ color: CHARCOAL, fontSize: "clamp(16px, 1.4vw, 18px)", lineHeight: 1.6, maxWidth: "46ch" }}
                        >
                          <span className="line-clamp-3">{s.description}</span>
                        </p>
                      )}
                      {s.meta && (
                        <p className="mt-4 font-sans text-xs tracking-wide" style={{ color: `${STEEL}99` }}>{s.meta}</p>
                      )}
                      <Link
                        to={s.href}
                        tabIndex={active ? 0 : -1}
                        aria-hidden={!active}
                        className="group mt-7 inline-flex items-center gap-2 font-sans text-sm font-semibold tracking-wide transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                        style={{ color: STEEL, ["--tw-ring-color"]: GOLD }}
                        data-testid={`slide-action-${i}`}
                      >
                        {s.type === "video" ? "Watch video" : "Read article"}
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" style={{ color: GOLD }} />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Prev / Next controls — near outer edges, vertically centered (desktop) / over media lower edge (mobile) */}
        {count > 1 && (
          <>
            <button
              onClick={prev}
              aria-label="Previous slide"
              data-testid="slider-prev"
              className={`${ctrlBase} absolute left-2 z-20 -translate-y-1/2 hover:bg-[#3F5968] hover:text-[#F6F1E8] lg:-left-5`}
              style={{ top: isMobile && arrowTop != null ? `${arrowTop}px` : "50%", backgroundColor: IVORY, borderColor: GOLD, color: STEEL, ["--tw-ring-color"]: GOLD }}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={next}
              aria-label="Next slide"
              data-testid="slider-next"
              className={`${ctrlBase} absolute right-2 z-20 -translate-y-1/2 hover:bg-[#3F5968] hover:text-[#F6F1E8] lg:-right-5`}
              style={{ top: isMobile && arrowTop != null ? `${arrowTop}px` : "50%", backgroundColor: IVORY, borderColor: GOLD, color: STEEL, ["--tw-ring-color"]: GOLD }}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}
      </div>

      {/* Position indicator — aligned with content grid */}
      {count > 1 && (
        <div className="mt-7 flex items-center gap-4" data-testid="slider-dots">
          <div className="flex items-center gap-2">
            {slides.map((s, i) => (
              <button
                key={s.id || i}
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                aria-current={i === index}
                data-testid={`slider-dot-${i}`}
                className="h-1.5 rounded-full transition-all duration-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                style={{
                  width: i === index ? 34 : 14,
                  backgroundColor: i === index ? GOLD : `${STEEL}40`,
                  ["--tw-ring-color"]: GOLD,
                }}
              />
            ))}
          </div>
          <span className="font-sans text-xs tabular-nums tracking-[0.18em]" style={{ color: `${STEEL}CC` }} data-testid="slider-counter">
            {String(index + 1).padStart(2, "0")} / {String(count).padStart(2, "0")}
          </span>
        </div>
      )}
    </section>
  );
}
